<script setup>
    import { ref, computed, onMounted } from 'vue';

    import { useResizeObserver } from '@vueuse/core';

    import { useSpotStore } from '@/stores/SpotStore';
    import { useUserStore } from '@/stores/UserStore';
    import { useCompleteReportStore } from '@/stores/CompleteReportStore';

    import CompleteReportHeaderPrimaryActions from '@/components/CompleteReportHeaderPrimaryActions.vue';
    import CompleteReportHeaderSecondaryActions from '@/components/CompleteReportHeaderSecondaryActions.vue';
    import ModalSaveSpotToProfile from '@/components/ModalSaveSpotToProfile.vue';
    import Icon from "@/components/icons/Icon.vue";

    const spotStore = useSpotStore();
    const userStore = useUserStore();
    const completeReportStore = useCompleteReportStore();

    const titleElement = ref();
    const locationElement = ref();
    const locationClipped = ref(false);
    const clippedAmount = ref(0);
    const showClipped = ref(false);
    const showProfileModal = ref(false);

    const spotName = computed(() => spotStore.isNowcast ? `${spotStore.spotName} <span class="jwx-page__title-tag">Nowcast</span>` : spotStore.spotName);
    const spotLocation = computed(() => {
      const locationParts = [];
      if (spotStore.spot.city) locationParts.push(spotStore.spot.city);
      if (spotStore.spot.county) locationParts.push(spotStore.spot.county);
      if (spotStore.spot.state) locationParts.push(spotStore.spot.state);
      if (spotStore.spot.country) locationParts.push(spotStore.spot.country);
      
      return locationParts.join(", ")
    });

    const hasLocation = computed(() => spotLocation.value === '' ? null : true);

    function setLocationClipped() {
      const locationElementEndPosition = locationElement.value.getBoundingClientRect().right;
      const titleElementEndPosition = titleElement.value.getBoundingClientRect().right;
      const endPositionDelta = titleElementEndPosition - locationElementEndPosition;

      locationClipped.value = endPositionDelta < 0;
      clippedAmount.value = locationClipped.value ? endPositionDelta - 16 : 0;
    }

    function handleShowClipped() {
      if (!locationClipped.value) return;

      showClipped.value = true;

      setTimeout(() => showClipped.value = false, 4000);
    }

    onMounted(() => {
      if (!locationElement.value) return;

      setLocationClipped();
    });

    useResizeObserver(locationElement, (entries) => {
      if (!locationElement.value) return;

      setLocationClipped();
    });
</script>
<template>
    <header class="jwx-page-header">
      <div class="jwx-page-header__main-content jwx-page-block jwx-page-block--padding-sm">
        <div ref="titleElement" class="jwx-page-header__title" :data-has-location="hasLocation">
          <h1 v-html="spotName" class="jwx-page-header__spot-name"></h1>
          <div
            v-if="hasLocation"
            class="jwx-page-header__location"
          >
            <Icon name="location-marker" class="jwx-page-header__location-icon" />
            <p
              ref="locationElement"
              @click="handleShowClipped"
              class="jwx-page-header__location-text"
              :class="{ 'show-clipped' : showClipped}"
              :style="`--clipped-amount: ${clippedAmount}px`"
            >
              {{ spotLocation }}
            </p>
          </div>
        </div>
        <p v-if="spotStore.statusMessage" class="jwx-page-header__meta"><span class="jwx-page__meta-label">Status Message:</span> {{ spotStore.statusMessage }}</p>
  
        <CompleteReportHeaderPrimaryActions
          v-model:showProfileModal="showProfileModal"
          v-model:showSettingsModal="completeReportStore.showSettingsModal"
        />
            
        <div v-if="spotStore.spotMessage" class="jwx-page-header__message">
          <p class="jwx-page-header__message-label">Special Note:</p>
          <p v-html="spotStore.spotMessage" class="jwx-page-header__message-text"></p>
        </div>
      </div>

      <CompleteReportHeaderSecondaryActions
        v-if="userStore.nonTrialMemberLevel > -1"
        v-model:showSettingsModal="completeReportStore.showSettingsModal"
        />
    </header>

    <ModalSaveSpotToProfile
      v-model:showProfileModal="showProfileModal"
      v-if="showProfileModal"
      :spotName="spotStore.spotName"
      :profiles="userStore.userProfiles.profiles"
    />
</template>